import dayjs from "dayjs";
import type { FormFieldMetadata } from "../hooks/useFormMetadata";
import { isISODateString, isTimeString, parseTimeString } from "./dateUtils";

/**
 * Coerce a raw Aras default_value string into the correct typed value
 * for the corresponding form field. Aras returns all default values as
 * strings (e.g. "1" for boolean true, "0" for boolean false).
 */
export const coerceArasDefaultValue = (
  rawValue: string,
  dataType: string,
): unknown => {
  const normalizedType = dataType.toLowerCase().replace(/\s+/g, "_");

  switch (normalizedType) {
    case "boolean": {
      const v = rawValue.trim().toLowerCase();
      return v === "1" || v === "true";
    }
    case "integer": {
      const n = parseInt(rawValue, 10);
      return Number.isFinite(n) ? n : undefined;
    }
    case "decimal":
    case "float": {
      const n = parseFloat(rawValue);
      return Number.isFinite(n) ? n : undefined;
    }
    case "date":
    case "datetime":
      return isISODateString(rawValue) ? dayjs(rawValue) : undefined;
    case "time":
      return isTimeString(rawValue) ? parseTimeString(rawValue) : undefined;
    default:
      return rawValue;
  }
};

/**
 * Build an object of typed initial values from field metadata default values.
 * Fields without a defaultValue (or whose default cannot be coerced) are skipped.
 */
export const buildInitialValuesFromDefaults = (
  fields: FormFieldMetadata[],
): Record<string, unknown> => {
  const initial: Record<string, unknown> = {};

  fields.forEach((field) => {
    const raw = field.defaultValue;
    if (raw === undefined || raw === null || raw === "") return;

    const value = coerceArasDefaultValue(raw, field.dataType);
    if (value === undefined) return;

    initial[field.mappedName] = value;
  });

  return initial;
};
